import {FC, useEffect} from "react";
import {UpsertSurgeryRequestsFormProps} from "../types";
import {DatePicker, Form, Input, Select} from "antd";
import TextArea from "antd/es/input/TextArea";
import "./styles.less";
import useFormService from "./formService";
import {VALIDATION_MESSAGES} from "../../../../enums/validationMessages";
import {DEFAULT_MAX_INPUT_LENGTH, DEFAULT_MAX_TEXTAREA_LENGTH} from "../../../../constants/validation";
import dayjs from "dayjs";
import {CalendarOutlined, TeamOutlined, UserOutlined} from "@ant-design/icons";

const UpsertSurgeryRequestsForm: FC<UpsertSurgeryRequestsFormProps> = ({controller}) => {
  const {form, createSurgeryRequestHandler, updateSurgeryRequestHandler, selectedItem} = controller;
  const {
    bloodTypeFieldOptions,
    onFinishHandler,
    staffFieldsOptions,
    anesthesiaFieldOptions,
    operationRoomSelectOptions
  } = useFormService({createSurgeryRequestHandler, updateSurgeryRequestHandler, selectedItem});

  useEffect(() => {
    if (selectedItem) {
      form.setFieldsValue({
        ...selectedItem,
        operationRoom: selectedItem.operationRoom?.$id,
        operationTime: dayjs(selectedItem.operationTime),
      });
    } else {
      form.resetFields();
    }
  }, [form, selectedItem]);

  const requiredRule = {required: true, message: VALIDATION_MESSAGES.REQUIRED};

  return <Form className="surgeryRequest_form" layout="vertical" form={form} onFinish={onFinishHandler}>
    <p className="surgeryRequest_form__description">
      Заповніть час, операційну, склад команди та основні дані пацієнта. Обов’язкові поля позначені зірочкою.
    </p>

    <section className="surgery-form-section surgery-form-section--planning">
      <header className="surgery-form-section__header">
        <span className="surgery-form-section__icon"><CalendarOutlined/></span>
        <span><small>Крок 1</small><strong>Планування операції</strong></span>
      </header>
      <div className="surgery-form-grid surgery-form-grid--two">
        <Form.Item rules={[requiredRule]} name="operationTime" label="Дата та час операції">
          <DatePicker
            size="large"
            format="DD.MM.YYYY HH:mm"
            placeholder="Оберіть дату та час"
            showTime={{format: "HH:mm", minuteStep: 10, hideDisabledOptions: true}}
            disabledTime={() => ({
              disabledHours: () => Array.from({length: 24}, (_, hour) => hour).filter((hour) => hour < 7 || hour > 19)
            })}
          />
        </Form.Item>
        <Form.Item rules={[requiredRule]} name="operationRoom" label="Операційна">
          <Select size="large" showSearch optionFilterProp="label" options={operationRoomSelectOptions}
                  placeholder="Оберіть операційну"/>
        </Form.Item>
      </div>
    </section>

    <div className="surgery-form-columns">
      <section className="surgery-form-section">
        <header className="surgery-form-section__header">
          <span className="surgery-form-section__icon"><TeamOutlined/></span>
          <span><small>Крок 2</small><strong>Операційна команда</strong></span>
        </header>
        <Form.Item rules={[requiredRule]} name="surgeon" label="Хірург">
          <Select size="large" mode="multiple" maxCount={1} options={staffFieldsOptions.surgeonOptions}
                  optionFilterProp="label" placeholder="Оберіть хірурга"/>
        </Form.Item>
        <Form.Item name="assistant" label="Асистенти">
          <Select size="large" allowClear mode="multiple" options={staffFieldsOptions.assistantOptions}
                  optionFilterProp="label" placeholder="Оберіть асистентів"/>
        </Form.Item>
        <Form.Item name="anesthesiologist" label="Анестезіолог">
          <Select size="large" allowClear mode="multiple" maxCount={1}
                  options={staffFieldsOptions.anesthesiologistOptions} optionFilterProp="label"
                  placeholder="Оберіть анестезіолога"/>
        </Form.Item>
        <Form.Item name="anesthesiaNurse" label="Сестра-анестезистка">
          <Select size="large" allowClear mode="multiple" maxCount={1}
                  options={staffFieldsOptions.anesthesiaNurseOptions} optionFilterProp="label"
                  placeholder="Оберіть сестру-анестезистку"/>
        </Form.Item>
        <Form.Item name="scrubNurse" label="Операційна сестра/брат">
          <Select size="large" allowClear mode="multiple" options={staffFieldsOptions.scrubNurseOptions}
                  optionFilterProp="label" placeholder="Оберіть персонал"/>
        </Form.Item>
      </section>

      <section className="surgery-form-section">
        <header className="surgery-form-section__header">
          <span className="surgery-form-section__icon"><UserOutlined/></span>
          <span><small>Крок 3</small><strong>Дані пацієнта</strong></span>
        </header>
        <Form.Item rules={[requiredRule, {
          max: DEFAULT_MAX_INPUT_LENGTH,
          message: `Максимум ${DEFAULT_MAX_INPUT_LENGTH} символів`,
        }]} name="patientName" label="ПІБ пацієнта">
          <Input size="large" placeholder="Введіть ПІБ пацієнта"/>
        </Form.Item>
        <div className="surgery-form-grid surgery-form-grid--two">
          <Form.Item name="bloodType" label="Група крові">
            <Select size="large" allowClear options={bloodTypeFieldOptions} placeholder="Оберіть групу"/>
          </Form.Item>
          <Form.Item name="anesthesia" label="Анестезія">
            <Select size="large" allowClear options={anesthesiaFieldOptions} placeholder="Оберіть тип"/>
          </Form.Item>
        </div>
        <Form.Item rules={[{
          max: DEFAULT_MAX_TEXTAREA_LENGTH,
          message: `Максимум ${DEFAULT_MAX_TEXTAREA_LENGTH} символів`,
        }]} name="diagnosis" label="Діагноз">
          <TextArea rows={3} showCount maxLength={DEFAULT_MAX_TEXTAREA_LENGTH} placeholder="Введіть діагноз"/>
        </Form.Item>
        <Form.Item rules={[requiredRule, {
          max: DEFAULT_MAX_TEXTAREA_LENGTH,
          message: `Максимум ${DEFAULT_MAX_TEXTAREA_LENGTH} символів`,
        }]} name="operation" label="Операція">
          <TextArea rows={3} showCount maxLength={DEFAULT_MAX_TEXTAREA_LENGTH} placeholder="Опишіть операцію"/>
        </Form.Item>
      </section>
    </div>
  </Form>;
};

export default UpsertSurgeryRequestsForm;
